import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CountUp } from "../common";
import { Info, X, PartyPopper } from "lucide-react";

const pick = (lang, hi, en) => (lang === "English" ? en : hi);
const TONE = { green: "#2f9e3a", amber: "#f2a30f", red: "#e11d48" };

export function FarmScoreCard({ score, language, item }) {
  const [open, setOpen] = useState(false);
  if (!score) return null;
  const pct = (score.score / score.max) * 100;
  const grade = pick(language, score.grade_hi, score.grade_en);

  return (
    <>
      <motion.button
        variants={item} onClick={() => setOpen(true)} data-testid="farm-score-card"
        className="text-left rounded-[22px] p-5 text-white relative overflow-hidden shadow-[0_10px_30px_rgba(23,63,30,0.35)] w-full tap active:scale-[0.99] transition-transform"
        style={{ background: "linear-gradient(150deg,#173f1e,#12311a)" }}>
        <div className="flex items-center justify-between">
          <p className="text-white/75 text-sm font-medium">{pick(language, "आपका Farm Score", "Your Farm Score")}</p>
          <span className="flex items-center gap-1 text-[11px] text-white/70 bg-white/10 rounded-full px-2 py-1"><Info className="h-3 w-3" />{pick(language, "कैसे?", "How?")}</span>
        </div>
        <div className="flex items-end gap-1 mt-1">
          <CountUp value={score.score} className="font-display text-[42px] font-extrabold leading-none" />
          <span className="text-white/70 text-lg mb-1">/{score.max}</span>
          <span className="ml-auto text-[#f2c230] text-sm font-semibold mb-1">{grade}</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/15 mt-3 overflow-hidden">
          <motion.div className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#f2c230,#2f9e3a)" }} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.1, ease: "easeOut" }} />
        </div>
        <p className="text-[13px] text-white/85 mt-3 flex items-center gap-1"><PartyPopper className="h-4 w-4 text-[#f2c230]" />{pick(language, "टैप करें — किस बात पर अंक मिले देखें", "Tap to see what drives your score")} ›</p>
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 z-[10040] bg-black/40 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.div
              className="fixed z-[10050] left-1/2 -translate-x-1/2 bottom-0 sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2 w-full sm:max-w-md bg-[#faf7ef] rounded-t-[28px] sm:rounded-[28px] p-5 shadow-2xl max-h-[85vh] overflow-y-auto"
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={{ type: "spring", stiffness: 240, damping: 26 }}
              data-testid="farm-score-breakdown">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <p className="font-display font-extrabold text-2xl text-[#173f1e]">{score.score}<span className="text-base text-[#6c7a67]">/{score.max}</span> · {grade}</p>
                  <p className="text-sm text-[#6c7a67]">{pick(language, "यह स्कोर 5 बातों से बनता है", "Your score is built from 5 factors")}</p>
                </div>
                <button onClick={() => setOpen(false)} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center tap" data-testid="score-close"><X className="h-4 w-4 text-[#173f1e]" /></button>
              </div>
              <div className="space-y-3 mt-4">
                {score.factors.map((f) => {
                  const fp = (f.points / f.max) * 100;
                  return (
                    <div key={f.key} className="rounded-2xl bg-white p-3.5 border border-black/5" data-testid={`score-factor-${f.key}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{f.emoji}</span>
                        <p className="font-semibold text-sm text-[#1c2b1e] flex-1">{pick(language, f.label_hi, f.label_en)}</p>
                        <span className="font-bold text-sm tabular-nums" style={{ color: TONE[f.tone] }}>{f.points}<span className="text-[#9aa79a] font-normal">/{f.max}</span></span>
                      </div>
                      <div className="h-1.5 rounded-full bg-black/5 mt-2 overflow-hidden">
                        <motion.div className="h-full rounded-full" style={{ background: TONE[f.tone] }} initial={{ width: 0 }} animate={{ width: `${fp}%` }} transition={{ duration: 0.7 }} />
                      </div>
                      <p className="text-[12px] text-[#6c7a67] mt-1.5">{pick(language, f.note_hi, f.note_en)}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
